import React, {useCallback, useEffect, useState} from "react";
import clsx from "clsx";
import {T, useTranslate} from "@tolgee/react";
import {formatDistanceToNow} from "date-fns";
import {
  ButtonBase,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DevicesIcon from "@material-ui/icons/Devices";
import {Roles} from "@evennt-tech/api-v2-core";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import {formatISODate} from "tools/formats/formatDate";
import {maskEmail} from "tools/formats/maskEmail";
import {maskPhone} from "tools/formats/maskPhone";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import {useTwoFactorSettings} from "store/two-factor/two-factor-settings.store";
import {useSmsTwoFactor} from "store/two-factor/sms.store";
import {useEmailTwoFactor} from "store/two-factor/email-two-factor.store";
import {useTotp} from "store/two-factor/totp.store";
import {usePasskeys} from "store/passkey/passkey.store";
import {useTrustedDevices} from "store/two-factor/trusted-device.store";
import {useIsShowLevelOrganiser} from "hooks";
import PasskeyManager from "view/components/PasskeyManager";
import AuthenticatorManager from "view/components/AuthenticatorManager";
import EmailTwoFactorManager from "view/components/EmailTwoFactorManager";
import SmsTwoFactorManager from "view/components/SmsTwoFactorManager";
import TrustedDevicesManager from "view/components/TrustedDevicesManager";
import ConfirmPasswordModal from "view/components/ConfirmPasswordModal";
import FlexBox from "view/components/Layout/FlexBox";
import useStyles from "./styles";
import {
  renderTwoFactorMethodIcon,
  TWO_FACTOR_METHOD_LABEL_KEY,
} from "./methods";

export type ViewState =
  | "menu"
  | "passkey"
  | "authenticator"
  | "email"
  | "sms"
  | "trustedDevices";

const METHOD_VIEW: Record<TwoFactorMethodType, ViewState> = {
  sms: "sms",
  totp: "authenticator",
  passkey: "passkey",
  email: "email",
};

const METHODS_ORDER: TwoFactorMethodType[] = [
  "sms",
  "totp",
  "passkey",
  "email",
];

type Props = {
  initialView?: ViewState;
};

const TwoFactorMethods: React.FC<Props> = ({initialView = "menu"}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const [view, setView] = useState<ViewState>(initialView);
  const [expanded, setExpanded] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const settings = useTwoFactorSettings((state) => state.settings);
  const fetchSettings = useTwoFactorSettings((state) => state.fetchSettings);
  const updatePreferredMethod = useTwoFactorSettings(
    (state) => state.updatePreferredMethod
  );
  const disable = useTwoFactorSettings((state) => state.disable);
  const {hasPassword, role} = useSelector(selectUser);

  const smsStatus = useSmsTwoFactor((state) => state.status);
  const fetchSmsStatus = useSmsTwoFactor((state) => state.fetchStatus);
  const emailStatus = useEmailTwoFactor((state) => state.status);
  const fetchEmailStatus = useEmailTwoFactor((state) => state.fetchStatus);
  const fetchTotpStatus = useTotp((state) => state.fetchStatus);
  const passkeyCredentials = usePasskeys((state) => state.credentials);
  const fetchPasskeyCredentials = usePasskeys(
    (state) => state.fetchCredentials
  );
  const trustedDevices = useTrustedDevices((state) => state.devices);
  const fetchTrustedDevices = useTrustedDevices((state) => state.fetchDevices);

  const fetchAll = useCallback(() => {
    fetchSettings();
    fetchSmsStatus();
    fetchEmailStatus();
    fetchTotpStatus();
    fetchPasskeyCredentials();
    fetchTrustedDevices();
  }, [
    fetchSettings,
    fetchSmsStatus,
    fetchEmailStatus,
    fetchTotpStatus,
    fetchPasskeyCredentials,
    fetchTrustedDevices,
  ]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isShowLevelOrganiser = useIsShowLevelOrganiser();

  const isEnabled = Boolean(settings?.enabled);
  const isExpanded = isEnabled || expanded;
  const isExhibitor = role === Roles.EXHIBITOR;
  const canManageTrustedDevices = isExhibitor || isShowLevelOrganiser;

  const handleConfirmDisable = async (password?: string) => {
    await disable(password);
    setIsDisabling(false);
    setExpanded(false);
    fetchAll();
  };

  const handleToggle = () => {
    if (isEnabled) {
      if (hasPassword) {
        setIsDisabling(true);
      } else {
        handleConfirmDisable();
      }
      return;
    }
    setExpanded((prev) => !prev);
  };

  const backToMenu = () => {
    setView("menu");
    fetchAll();
  };

  const handleOpenTrustedDevices = () => {
    setView("trustedDevices");
  };

  if (view === "passkey") return <PasskeyManager onBack={backToMenu} />;
  if (view === "authenticator") {
    return <AuthenticatorManager onBack={backToMenu} />;
  }
  if (view === "email") {
    return <EmailTwoFactorManager onBack={backToMenu} />;
  }
  if (view === "sms") {
    return <SmsTwoFactorManager onBack={backToMenu} />;
  }
  if (view === "trustedDevices") {
    return <TrustedDevicesManager onBack={backToMenu} />;
  }

  const isConfigured = (method: TwoFactorMethodType) =>
    settings?.methods.find((status) => status.method === method)?.configured ??
    false;

  const chipLabel = (method: TwoFactorMethodType): string | null => {
    if (!isConfigured(method)) return null;
    if (method === "sms")
      return smsStatus?.phone
        ? maskPhone(smsStatus.phone)
        : t("common.two_factor.added");
    if (method === "email")
      return emailStatus?.email
        ? maskEmail(emailStatus.email)
        : t("common.two_factor.added");
    if (method === "passkey") {
      const count = passkeyCredentials?.length ?? 0;
      return count > 1
        ? t("common.two_factor.count_added", {count})
        : t("common.two_factor.added");
    }
    return t("common.two_factor.added");
  };

  const configuredMethods = METHODS_ORDER.filter(isConfigured);
  const lastDevice = trustedDevices?.[0] ?? null;

  return (
    <>
      <div className={classes.statusRow}>
        <div className={classes.statusSwitch}>
          <Switch
            checked={isExpanded}
            onChange={handleToggle}
            color="primary"
          />
          <Typography
            className={clsx(
              classes.statusLabel,
              !isExpanded && classes.statusLabelOff
            )}
          >
            <T keyName="common.two_factor.enable_switch" />
          </Typography>
        </div>
        {settings?.enabled && settings.enabledSince && (
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label={t("common.two_factor.since", {
              date: formatISODate(settings.enabledSince, "d MMM yyyy", locale),
            })}
            className={classes.sinceChip}
          />
        )}
      </div>

      {isExpanded && (
        <>
          <Typography className={classes.subtitle}>
            <T keyName="common.two_factor.select_method" />
          </Typography>
          {METHODS_ORDER.map((method) => (
            <ButtonBase
              key={method}
              className={classes.methodRow}
              onClick={() => setView(METHOD_VIEW[method])}
            >
              <FlexBox alignCenter gap={1.5}>
                <div className={classes.iconBox}>
                  {renderTwoFactorMethodIcon(method)}
                </div>
                <div className={classes.methodContent}>
                  <Typography className={classes.methodTitle}>
                    {t(TWO_FACTOR_METHOD_LABEL_KEY[method])}
                  </Typography>
                </div>
                {chipLabel(method) && (
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon />}
                    label={chipLabel(method)}
                    className={classes.methodChip}
                  />
                )}
              </FlexBox>
              <ArrowForwardIcon
                fontSize="small"
                className={classes.arrowIcon}
              />
            </ButtonBase>
          ))}

          <Divider className={classes.divider} />

          {configuredMethods.length > 0 && (
            <FormControl
              fullWidth
              variant="outlined"
              className={classes.preferredControl}
            >
              <InputLabel id="preferred-two-factor-method-label">
                {t("common.two_factor.preferred_method")}
              </InputLabel>
              <Select
                labelId="preferred-two-factor-method-label"
                label={t("common.two_factor.preferred_method")}
                value={settings?.preferredMethod ?? ""}
                onChange={(event) =>
                  updatePreferredMethod(
                    event.target.value as TwoFactorMethodType
                  )
                }
              >
                {configuredMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {t(TWO_FACTOR_METHOD_LABEL_KEY[method])}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isEnabled && !!lastDevice && canManageTrustedDevices && (
            <ButtonBase
              className={classes.trustedDevicesCard}
              onClick={handleOpenTrustedDevices}
            >
              <div className={classes.trustedDevicesContent}>
                <Typography className={classes.trustedDevicesTitle}>
                  <T keyName="common.two_factor.trusted_devices.title" />
                </Typography>
                <div className={classes.trustedDevicesSubtitleRow}>
                  {lastDevice ? (
                    <>
                      <Typography className={classes.trustedDevicesSubtitle}>
                        <T keyName="common.two_factor.last_session" />
                      </Typography>
                      <DevicesIcon
                        fontSize="small"
                        className={classes.trustedDevicesIcon}
                      />
                      <Typography className={classes.trustedDevicesSubtitle}>
                        {lastDevice.deviceLabel ||
                          t("common.two_factor.unknown_device")}
                        {" · "}
                        {formatDistanceToNow(new Date(lastDevice.lastSeenAt), {
                          addSuffix: true,
                        })}
                      </Typography>
                    </>
                  ) : (
                    <Typography className={classes.trustedDevicesSubtitle}>
                      <T keyName="common.two_factor.no_trusted_devices_short" />
                    </Typography>
                  )}
                </div>
              </div>
              <ArrowForwardIcon
                fontSize="small"
                className={classes.arrowIcon}
              />
            </ButtonBase>
          )}
        </>
      )}

      {isDisabling && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.disable_title")}
          submitTitle={t("common.action.disable")}
          onClose={() => setIsDisabling(false)}
          onConfirm={handleConfirmDisable}
        />
      )}
    </>
  );
};

export default TwoFactorMethods;
